import {defineMessages} from 'react-intl'

/**
 * Catálogo del Centro de Recursos (`/resource-center`): el listado con sus dos
 * vistas —tabla y cuadrícula—, el árbol de carpetas, los cuatro modales de
 * recurso y carpeta, el de etiquetas y los visores de contenido.
 *
 * Namespace propio `resources.*`. Lo transversal sigue en `common.*`.
 *
 * No se traducen: los nombres de recurso, carpeta y etiqueta —los escribe la
 * agencia—, ni los nombres de archivo.
 */
export const RESOURCE_MESSAGES = defineMessages({
  // ── Cabecera ──
  title: {id: 'MENU.item.resourceCenter', defaultMessage: 'Resource Center'},
  subtitle: {
    id: 'resources.page.subtitle',
    defaultMessage: 'Videos, documents, templates and guides for your team',
  },
  hideFolders: {id: 'resources.page.hideFolders', defaultMessage: 'Hide folders'},
  showFolders: {id: 'resources.page.showFolders', defaultMessage: 'Show folders'},
  switchToGrid: {id: 'resources.page.switchToGrid', defaultMessage: 'Switch to grid view'},
  switchToTable: {id: 'resources.page.switchToTable', defaultMessage: 'Switch to table view'},
  manageTagsTooltip: {
    id: 'resources.page.manageTagsTooltip',
    defaultMessage: 'Create and rename resource tags',
  },
  manageTags: {id: 'resources.page.manageTags', defaultMessage: 'Manage Tags'},
  addResource: {id: 'resources.page.addResource', defaultMessage: 'Add Resource'},
  errLoading: {
    id: 'resources.page.error.loading',
    defaultMessage: 'Error loading resources: {message}',
  },

  // ── Filtros ──
  allResources: {id: 'resources.page.allResources', defaultMessage: 'All resources'},
  includeSubfolders: {
    id: 'resources.page.includeSubfolders',
    defaultMessage: 'Include resources from subfolders',
  },
  searchByName: {id: 'resources.filters.searchByName', defaultMessage: 'Search by name'},
  searchPlaceholder: {
    id: 'resources.filters.searchPlaceholder',
    defaultMessage: 'Type to search...',
  },
  filterByType: {id: 'resources.filters.byType', defaultMessage: 'Filter by type'},
  allTypes: {id: 'resources.filters.allTypes', defaultMessage: 'All types'},
  filterByTag: {id: 'resources.filters.byTag', defaultMessage: 'Filter by tag'},
  allTags: {id: 'resources.filters.allTags', defaultMessage: 'All tags'},
  search: {id: 'common.action.search', defaultMessage: 'Search'},

  // ── Selección múltiple ──
  selectedToMove: {
    id: 'resources.page.selectedToMove',
    defaultMessage: '<b>{count}</b> selected — drag to a folder to move',
  },
  clearSelection: {id: 'resources.page.clearSelection', defaultMessage: 'Clear selection'},
  selectAllOnPage: {
    id: 'resources.page.selectAllOnPage',
    defaultMessage: 'Select all on this page',
  },
  dragToFolder: {id: 'resources.page.dragToFolder', defaultMessage: 'Drag to a folder to move'},
  selectToMove: {id: 'resources.page.selectToMove', defaultMessage: 'Select to move'},
  errMoving: {id: 'resources.page.error.moving', defaultMessage: 'Error moving resources'},

  // ── Tabla ──
  show: {id: 'common.pagination.show', defaultMessage: 'Show'},
  entriesLabel: {id: 'resources.page.entriesLabel', defaultMessage: 'entries'},
  all: {id: 'common.pagination.all', defaultMessage: 'All'},
  searching: {id: 'resources.page.searching', defaultMessage: 'Searching...'},
  searchingInFolder: {
    id: 'resources.page.searchingInFolder',
    defaultMessage: 'Searching in this folder and its subfolders',
  },
  showingEmpty: {id: 'resources.page.showingEmpty', defaultMessage: 'Showing 0 entries'},
  showingRange: {
    id: 'resources.page.showingRange',
    defaultMessage: 'Showing {from} to {to} of {total} entries',
  },
  pageOf: {id: 'resources.page.pageOf', defaultMessage: 'Page {page} of {total}'},
  showingScoped: {
    id: 'resources.page.showingScoped',
    defaultMessage: 'Showing {from} to {to} of {total} {scope}',
  },
  scopeFiltered: {id: 'resources.page.scope.filtered', defaultMessage: 'filtered results'},
  scopeFolder: {
    id: 'resources.page.scope.folder',
    defaultMessage: 'resources in this folder',
  },
  scopeUncategorized: {
    id: 'resources.page.scope.uncategorized',
    defaultMessage: 'uncategorized resources',
  },
  colName: {id: 'common.field.name', defaultMessage: 'Name'},
  colType: {id: 'common.field.type', defaultMessage: 'Type'},
  colCreatedBy: {id: 'resources.col.createdBy', defaultMessage: 'Created By'},
  colViews: {id: 'resources.col.views', defaultMessage: 'Views'},
  colActions: {id: 'common.field.actions', defaultMessage: 'Actions'},
  openFolderTooltip: {
    id: 'resources.page.openFolderTooltip',
    defaultMessage: 'Open folder · Drop resources here to move them',
  },
  clickToViewTooltip: {
    id: 'resources.page.clickToViewTooltip',
    defaultMessage: 'Click to view · Drag to a folder to move',
  },
  itemCount: {
    id: 'resources.page.itemCount',
    defaultMessage: '{count, plural, one {# item} other {# items}}',
  },
  subfolderCount: {
    id: 'resources.page.subfolderCount',
    defaultMessage: '{count, plural, one {# subfolder} other {# subfolders}}',
  },
  noResources: {id: 'resources.page.noResources', defaultMessage: 'No resources found'},
  noResourcesHint: {
    id: 'resources.page.noResourcesHint',
    defaultMessage: 'Try adjusting your filters or add a new resource',
  },
  viewResource: {id: 'resources.action.view', defaultMessage: 'View resource'},
  view: {id: 'common.action.view', defaultMessage: 'View'},
  download: {id: 'leads.kanban.downloadTooltip', defaultMessage: 'Download'},
  edit: {id: 'common.action.edit', defaultMessage: 'Edit'},
  del: {id: 'common.action.delete', defaultMessage: 'Delete'},
  unknownUser: {id: 'resources.page.unknownUser', defaultMessage: 'Unknown'},

  // ── Árbol de carpetas ──
  folders: {id: 'resources.tree.folders', defaultMessage: 'Folders'},
  newFolderTooltip: {id: 'resources.tree.newFolderTooltip', defaultMessage: 'New folder'},
  rootTooltip: {
    id: 'resources.tree.rootTooltip',
    defaultMessage: 'Root — folders and uncategorized resources',
  },
  allResourcesNode: {id: 'resources.tree.allResources', defaultMessage: 'All resources'},
  noFolders: {id: 'resources.tree.noFolders', defaultMessage: 'No folders yet.'},
  noFoldersHint: {
    id: 'resources.tree.noFoldersHint',
    defaultMessage: 'Create one to organize your resources.',
  },
  newSubfolder: {id: 'resources.tree.newSubfolder', defaultMessage: 'New subfolder'},
  renameMove: {id: 'resources.tree.renameMove', defaultMessage: 'Rename / Move'},

  // ── Modal de carpeta ──
  newFolder: {id: 'resources.folder.new', defaultMessage: 'New Folder'},
  editFolder: {id: 'resources.folder.edit', defaultMessage: 'Edit Folder'},
  folderNamePlaceholder: {
    id: 'resources.folder.namePlaceholder',
    defaultMessage: 'Folder name',
  },
  parentFolder: {id: 'resources.folder.parent', defaultMessage: 'Parent folder'},
  noneRootFolder: {id: 'resources.folder.noneRoot', defaultMessage: 'None (root folder)'},
  parentHint: {
    id: 'resources.folder.parentHint',
    defaultMessage: 'Leave empty to create a top-level folder',
  },
  orderHint: {id: 'resources.folder.orderHint', defaultMessage: 'Lower numbers appear first'},
  parentMoveHint: {
    id: 'resources.folder.parentMoveHint',
    defaultMessage:
      'Change the parent to move this folder. A folder cannot be moved inside itself.',
  },
  createFolder: {id: 'resources.folder.create', defaultMessage: 'Create Folder'},
  orderHintPriority: {
    id: 'resources.form.orderHintPriority',
    defaultMessage: 'Lower numbers appear first (0 = highest priority)',
  },
  createResource: {id: 'resources.form.create', defaultMessage: 'Create Resource'},
  updateResource: {id: 'resources.form.update', defaultMessage: 'Update Resource'},
  updating: {id: 'common.state.updating', defaultMessage: 'Updating...'},
  dragOrClickNew: {
    id: 'resources.form.dragOrClickNew',
    defaultMessage: 'Drag & drop new file here, paste (Ctrl+V), or click below to select',
  },
  toastFolderCreated: {
    id: 'resources.folder.toast.created',
    defaultMessage: 'Folder created successfully',
  },
  errCreateFolder: {
    id: 'resources.folder.error.create',
    defaultMessage: 'Error creating folder',
  },
  toastFolderUpdated: {
    id: 'resources.folder.toast.updated',
    defaultMessage: 'Folder updated successfully',
  },
  errUpdateFolder: {
    id: 'resources.folder.error.update',
    defaultMessage: 'Error updating folder',
  },

  // ── Borrar carpeta ──
  deleteFolder: {id: 'resources.folder.delete', defaultMessage: 'Delete Folder'},
  confirmDeleteFolder: {
    id: 'resources.folder.confirmDelete',
    defaultMessage: 'Are you sure you want to delete the folder <b>“{name}”</b>?',
  },
  folderHasSubfolders: {
    id: 'resources.folder.hasSubfolders',
    defaultMessage:
      'This folder has <b>{count}</b> subfolder(s). You must move or delete them first.',
  },
  folderResourcesKept: {
    id: 'resources.folder.resourcesKept',
    defaultMessage:
      'Resources inside this folder will <b>not</b> be deleted — they will be moved to “Uncategorized”.',
  },
  folderResourceCount: {
    id: 'resources.folder.resourceCount',
    defaultMessage: 'This folder currently contains <b>{count}</b> resource(s).',
  },
  yesDeleteFolder: {id: 'resources.folder.yesDelete', defaultMessage: 'Yes, Delete Folder'},
  toastFolderDeleted: {
    id: 'resources.folder.toast.deleted',
    defaultMessage: 'Folder deleted successfully',
  },
  toastResourceDeleted: {
    id: 'resources.delete.toast.deleted',
    defaultMessage: 'Resource deleted successfully',
  },
  toastResourceMoved: {
    id: 'resources.page.toast.moved',
    defaultMessage:
      '{count, plural, one {Resource moved successfully} other {# resources moved successfully}}',
  },
  errDeleteFolder: {
    id: 'resources.folder.error.delete',
    defaultMessage: 'Error deleting folder',
  },

  // ── Modal de recurso ──
  addNewResource: {id: 'resources.form.addNew', defaultMessage: 'Add New Resource'},
  editResource: {id: 'resources.form.edit', defaultMessage: 'Edit Resource'},
  uploading: {id: 'resources.form.uploading', defaultMessage: 'Uploading...'},
  resourceType: {id: 'resources.form.resourceType', defaultMessage: 'Resource Type'},
  uploadFile: {id: 'resources.form.uploadFile', defaultMessage: 'Upload File'},
  externalUrl: {
    id: 'resources.form.externalUrl',
    defaultMessage: 'External URL (Loom, YouTube, etc)',
  },
  file: {id: 'contacts.profile.documents.col.file', defaultMessage: 'File'},
  replaceFile: {id: 'resources.form.replaceFile', defaultMessage: 'Replace File (Optional)'},
  dropFileHere: {id: 'resources.form.dropFileHere', defaultMessage: 'Drop file here...'},
  dragOrClick: {
    id: 'resources.form.dragOrClick',
    defaultMessage:
      'Drag & drop file here, paste from clipboard (Ctrl+V), or click below to select',
  },
  previewAlt: {id: 'resources.form.previewAlt', defaultMessage: 'Preview'},
  thumbnailAlt: {id: 'resources.form.thumbnailAlt', defaultMessage: 'Thumbnail preview'},
  urlPlaceholder: {
    id: 'resources.form.urlPlaceholder',
    defaultMessage: 'https://www.loom.com/share/...',
  },
  url: {id: 'resources.form.url', defaultMessage: 'URL'},
  urlSupported: {
    id: 'resources.form.urlSupported',
    defaultMessage: 'Supported: Loom, YouTube, Vimeo, or any external link',
  },
  urlHint: {
    id: 'resources.form.urlHint',
    defaultMessage: 'Paste the share link from Loom, YouTube, Vimeo or any other site',
  },
  namePlaceholder: {id: 'resources.form.namePlaceholder', defaultMessage: 'Resource name'},
  descriptionPlaceholder: {
    id: 'resources.form.descriptionPlaceholder',
    defaultMessage: 'Brief description of the resource...',
  },
  thumbnail: {id: 'resources.form.thumbnail', defaultMessage: 'Thumbnail (optional)'},
  thumbnailHint: {
    id: 'resources.form.thumbnailHint',
    defaultMessage:
      'Image shown as preview in the grid view (falls back to an icon if empty)',
  },
  currentThumbnail: {id: 'resources.form.currentThumbnail', defaultMessage: 'Current'},
  tag: {id: 'comms.tags.label', defaultMessage: 'Tag'},
  newTag: {id: 'resources.form.newTag', defaultMessage: 'New tag'},
  createNewTag: {id: 'resources.form.createNewTag', defaultMessage: 'Create a new tag'},
  selectTag: {id: 'resources.form.selectTag', defaultMessage: 'Select a tag...'},
  noFolderUncategorized: {
    id: 'resources.form.noFolderUncategorized',
    defaultMessage: 'No folder (uncategorized)',
  },
  commentsPlaceholder: {
    id: 'resources.form.commentsPlaceholder',
    defaultMessage: 'Additional notes or instructions...',
  },
  activeVisible: {
    id: 'resources.form.activeVisible',
    defaultMessage: 'Active (visible to users)',
  },
  folder: {id: 'academy.courseForm.folder', defaultMessage: 'Folder'},
  currentResource: {id: 'resources.form.currentResource', defaultMessage: 'Current resource:'},
  isUrlResource: {
    id: 'resources.form.isUrlResource',
    defaultMessage: 'This is a URL resource.',
  },
  isFileResource: {
    id: 'resources.form.isFileResource',
    defaultMessage: 'This is a file resource.',
  },
  switchHint: {
    id: 'resources.form.switchHint',
    defaultMessage: 'You can change the type below or keep it as is.',
  },
  newFile: {id: 'resources.form.newFile', defaultMessage: 'New file'},
  currentFile: {id: 'resources.form.currentFile', defaultMessage: 'Current file'},
  totalViews: {id: 'resources.form.totalViews', defaultMessage: 'Total Views'},
  created: {id: 'common.field.created', defaultMessage: 'Created'},
  lastUpdated: {id: 'resources.form.lastUpdated', defaultMessage: 'Last Updated'},
  toastFilePasted: {id: 'resources.form.toast.filePasted', defaultMessage: 'File pasted'},
  toastResourceCreated: {
    id: 'resources.form.toast.created',
    defaultMessage: 'Resource created successfully',
  },
  toastResourceUpdated: {
    id: 'resources.form.toast.updated',
    defaultMessage: 'Resource updated successfully',
  },
  errUpload: {id: 'resources.form.error.upload', defaultMessage: 'Error uploading resource'},
  errUpdateResource: {
    id: 'resources.form.error.update',
    defaultMessage: 'Error updating resource',
  },
  warnSelectFile: {id: 'resources.form.warn.selectFile', defaultMessage: 'Please select a file'},
  warnEnterUrl: {id: 'resources.form.warn.enterUrl', defaultMessage: 'Please enter a URL'},

  // ── Validación del formulario de recurso ──
  vNameRequired: {id: 'validation.name.required', defaultMessage: 'Name is required'},
  vTypeRequired: {
    id: 'resources.form.validation.type',
    defaultMessage: 'Resource type is required',
  },
  vFileRequired: {
    id: 'resources.form.validation.file',
    defaultMessage: 'File is required when using file mode',
  },
  vUrlRequired: {
    id: 'resources.form.validation.url',
    defaultMessage: 'URL is required when using URL mode',
  },
  vUrlInvalid: {id: 'resources.form.validation.urlInvalid', defaultMessage: 'Must be a valid URL'},

  // ── Borrar recurso ──
  deleteResource: {id: 'resources.delete.title', defaultMessage: 'Delete Resource'},
  deleteWarning: {
    id: 'resources.delete.warning',
    defaultMessage:
      '<b>Warning:</b> This action cannot be undone. The file will be permanently deleted from the server.',
  },
  confirmDeleteResource: {
    id: 'resources.delete.confirm',
    defaultMessage: 'Are you sure you want to delete this resource?',
  },
  labelFile: {id: 'resources.delete.labelFile', defaultMessage: 'File:'},
  labelType: {id: 'resources.delete.labelType', defaultMessage: 'Type:'},
  labelCreated: {id: 'resources.delete.labelCreated', defaultMessage: 'Created:'},
  labelViews: {id: 'resources.delete.labelViews', defaultMessage: 'Views:'},
  viewsTimes: {
    id: 'resources.delete.viewsTimes',
    defaultMessage: '{count, plural, one {# time} other {# times}}',
  },
  labelCreatedBy: {id: 'resources.delete.labelCreatedBy', defaultMessage: 'Created by:'},
  labelTag: {id: 'resources.delete.labelTag', defaultMessage: 'Tag:'},
  whatWillBeDeleted: {
    id: 'resources.delete.whatWillBeDeleted',
    defaultMessage: 'What will be deleted:',
  },
  bulletRecord: {
    id: 'resources.delete.bullet.record',
    defaultMessage: 'The database record for this resource',
  },
  bulletFile: {id: 'resources.delete.bullet.file', defaultMessage: 'The physical file:'},
  bulletMetadata: {
    id: 'resources.delete.bullet.metadata',
    defaultMessage: 'All metadata and view statistics',
  },
  yesDeleteResource: {
    id: 'resources.delete.yes',
    defaultMessage: 'Yes, Delete Resource',
  },
  unknownResource: {
    id: 'resources.delete.unknownResource',
    defaultMessage: 'Unknown resource',
  },
  errDeleteResource: {
    id: 'resources.delete.error',
    defaultMessage: 'Error deleting resource',
  },

  // ── Etiquetas ──
  newTagPlaceholder: {id: 'resources.tags.newPlaceholder', defaultMessage: 'New tag name...'},
  addTag: {id: 'resources.tags.add', defaultMessage: 'Add Tag'},
  existingTags: {id: 'resources.tags.existing', defaultMessage: 'Existing tags ({count})'},
  noTagsYet: {
    id: 'resources.tags.noneYet',
    defaultMessage: 'No tags yet — create the first one above',
  },
  renameTag: {id: 'resources.tags.rename', defaultMessage: 'Rename tag'},
  warnTagExists: {
    id: 'resources.tags.warn.exists',
    defaultMessage: 'A tag with that name already exists',
  },
  toastTagCreated: {id: 'resources.tags.toast.created', defaultMessage: 'Tag created successfully'},
  errCreateTag: {id: 'resources.tags.error.create', defaultMessage: 'Error creating tag'},
  errCouldNotCreateTag: {
    id: 'resources.tags.error.couldNotCreate',
    defaultMessage: 'Could not create tag',
  },
  toastTagRenamed: {id: 'resources.tags.toast.renamed', defaultMessage: 'Tag renamed successfully'},
  errRenameTag: {id: 'resources.tags.error.rename', defaultMessage: 'Error renaming tag'},
  errCouldNotRenameTag: {
    id: 'resources.tags.error.couldNotRename',
    defaultMessage: 'Could not rename tag',
  },

  // ── Visores ──
  description: {id: 'resources.viewer.description', defaultMessage: 'Description:'},
  noVideoSupport: {
    id: 'resources.viewer.noVideoSupport',
    defaultMessage: 'Your browser does not support video playback.',
  },
  troublePlaying: {
    id: 'resources.viewer.troublePlaying',
    defaultMessage: 'Having trouble playing it? Download / open in a new tab',
  },
  fileResourceHint: {
    id: 'resources.viewer.fileResourceHint',
    defaultMessage: 'This is a file resource. Click the button below to open or download it.',
  },
  openInNewTab: {id: 'resources.viewer.openInNewTab', defaultMessage: 'Open File in New Tab'},
  openNewTab: {id: 'resources.viewer.openNewTab', defaultMessage: 'Open in New Tab'},
  openExternalLink: {
    id: 'resources.viewer.openExternalLink',
    defaultMessage: 'Open External Link',
  },
  videoPreviewTitle: {
    id: 'resources.viewer.videoPreviewTitle',
    defaultMessage: '{platform} video preview',
  },
  fileIconAlt: {id: 'resources.viewer.fileIconAlt', defaultMessage: 'file'},

  // ── Tipos de archivo de la vista previa ──
  fileDocument: {id: 'resources.fileType.document', defaultMessage: 'Document'},
  fileWord: {id: 'resources.fileType.word', defaultMessage: 'Word Document'},
  fileExcel: {id: 'resources.fileType.excel', defaultMessage: 'Excel Spreadsheet'},
  filePowerPoint: {id: 'resources.fileType.powerPoint', defaultMessage: 'PowerPoint'},
  fileVideo: {id: 'resources.fileType.video', defaultMessage: 'Video'},
  filePdf: {id: 'resources.fileType.pdf', defaultMessage: 'PDF Document'},
  fileAudio: {id: 'resources.fileType.audio', defaultMessage: 'Audio'},
  fileText: {id: 'resources.fileType.text', defaultMessage: 'Text File'},
  fileCompressed: {id: 'resources.fileType.compressed', defaultMessage: 'Compressed'},

  // ── Reusadas ──
  loading: {id: 'common.loading', defaultMessage: 'Loading...'},
  breadcrumb: {id: 'common.a11y.breadcrumb', defaultMessage: 'breadcrumb'},
  name: {id: 'common.field.name', defaultMessage: 'Name'},
  descriptionField: {id: 'common.field.description', defaultMessage: 'Description'},
  comments: {id: 'common.field.comments', defaultMessage: 'Comments'},
  displayOrder: {id: 'common.field.displayOrder', defaultMessage: 'Display Order'},
  cancel: {id: 'common.action.cancel', defaultMessage: 'Cancel'},
  close: {id: 'common.action.close', defaultMessage: 'Close'},
  done: {id: 'common.action.done', defaultMessage: 'Done'},
  saveChanges: {id: 'common.action.saveChanges', defaultMessage: 'Save changes'},
  saving: {id: 'common.state.saving', defaultMessage: 'Saving...'},
  creating: {id: 'common.state.creating', defaultMessage: 'Creating...'},
  deleting: {id: 'common.state.deleting', defaultMessage: 'Deleting...'},
  notAvailable: {id: 'common.value.notAvailable', defaultMessage: 'N/A'},
})

/**
 * Tipos de recurso. **El `value` viaja a la API**; solo se traduce la etiqueta.
 * `FAQ` es una sigla y se escribe igual en los dos idiomas.
 */
export const RESOURCE_TYPE_LABELS = defineMessages({
  video: {id: 'resources.type.video', defaultMessage: 'Video Tutorial'},
  document: {id: 'resources.type.document', defaultMessage: 'Document'},
  template: {id: 'resources.type.template', defaultMessage: 'Template'},
  format: {id: 'resources.type.format', defaultMessage: 'Format'},
  guide: {id: 'resources.type.guide', defaultMessage: 'Guide'},
  faq: {id: 'resources.type.faq', defaultMessage: 'FAQ'},
  other: {id: 'resources.type.other', defaultMessage: 'Other'},
})

/**
 * El backend devuelve el mensaje ya redactado; solo se traduce el prefijo.
 * Va **dentro** de `defineMessages`: fuera, formatjs no lo extrae.
 */
export const BACKEND_ERROR = defineMessages({
  withMessage: {id: 'common.error.withMessage', defaultMessage: 'Error: {message}'},
}).withMessage

/** Marca de campo obligatorio y separadores. Símbolos, no texto. */
export const REQUIRED_MARK = '*'
export const DOT_SEP = ' · '
export const DASH = '-'
/** Espacio entre una etiqueta y su valor. */
export const SPACE = ' '
export const KB_UNIT = ' KB'
